import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import type {
  AuthLoginResult,
  AuthUser,
  ScopeNode,
  UserRole,
} from '../models/operator.model';
import { DashboardApiService } from './dashboard-api.service';

const STORAGE_KEY = 'ah-dash-user';

const MOCK_SCOPES: ScopeNode[] = [
  {
    id: 'scope-arcadehaus',
    slug: 'arcadehaus',
    name: 'ArcadeHaus',
    type: 'organization',
    parentScopeId: null,
    sortOrder: 10,
    isRoot: false,
    children: [
      {
        id: 'scope-arcade-unit-01',
        slug: 'arcade-unit-01',
        name: 'Arcade Unit 01',
        type: 'physical-place',
        parentScopeId: 'scope-arcadehaus',
        sortOrder: 20,
        isRoot: false,
        children: [],
      },
    ],
  },
];

const MOCK_OPERATORS = [
  {
    email: 'admin@arcadehaus.com',
    password: 'arcade2024!',
    name: 'Alex Rivera',
    firstName: 'Alex',
    lastName: 'Rivera',
    baseRoles: ['developer'] as UserRole[],
  },
  {
    email: 'ops@arcadehaus.com',
    password: 'ops2024!',
    name: 'Jordan Lee',
    firstName: 'Jordan',
    lastName: 'Lee',
    baseRoles: ['partner'] as UserRole[],
  },
  {
    email: 'tech@arcadehaus.com',
    password: 'tech2024!',
    name: 'Sam Chen',
    firstName: 'Sam',
    lastName: 'Chen',
    baseRoles: ['client'] as UserRole[],
  },
];

function flattenScopes(scopes: ScopeNode[]): ScopeNode[] {
  const nodes: ScopeNode[] = [];
  for (const scope of scopes) {
    nodes.push(scope);
    nodes.push(...flattenScopes(scope.children));
  }
  return nodes;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly api = inject(DashboardApiService);
  private readonly _user = signal<AuthUser | null>(this.loadFromStorage());
  private readonly _sessionRefreshing = signal(false);

  readonly user = this._user.asReadonly();
  readonly sessionRefreshing = this._sessionRefreshing.asReadonly();
  readonly selectedScope = computed(() => {
    const user = this._user();
    if (!user?.selectedScopeId) {
      return null;
    }

    return flattenScopes(user.accessibleScopes).find((scope) => scope.id === user.selectedScopeId) ?? null;
  });

  private loadFromStorage(): AuthUser | null {
    try {
      const stored = localStorage.getItem(STORAGE_KEY) ?? sessionStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) as AuthUser : null;
    } catch {
      return null;
    }
  }

  private persistUser(user: AuthUser, keepLoggedIn: boolean): void {
    this._user.set(user);
    const storage = keepLoggedIn ? localStorage : sessionStorage;
    sessionStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_KEY);
    storage.setItem(STORAGE_KEY, JSON.stringify(user));
  }

  private isCurrentSessionPersistent(): boolean {
    return localStorage.getItem(STORAGE_KEY) !== null;
  }

  private fakeUserFor(email: string, password: string): AuthUser | null {
    const operator = MOCK_OPERATORS.find(
      (candidate) => candidate.email.toLowerCase() === email.toLowerCase() && candidate.password === password,
    );

    if (!operator) {
      return null;
    }

    return {
      authUserId: `fake-${operator.email}`,
      appUserId: `fake-app-${operator.email}`,
      email: operator.email,
      firstName: operator.firstName,
      lastName: operator.lastName,
      name: operator.name,
      status: 'active',
      baseRoles: operator.baseRoles,
      effectiveRoles: operator.baseRoles,
      isGenesis: false,
      isOwnerPlaceholder: false,
      canManageSettings: operator.baseRoles.includes('developer'),
      hasGlobalScopeAccess: false,
      selectedScopeId: 'scope-arcadehaus',
      accessibleScopes: MOCK_SCOPES,
      passwordChangeRequired: false,
    };
  }

  login(email: string, password: string, keepLoggedIn: boolean): Observable<AuthLoginResult> {
    if (this.api.isFakeShowcase) {
      const user = this.fakeUserFor(email, password);
      if (!user) {
        return of({ ok: false, passwordChangeRequired: false });
      }

      this.persistUser(user, keepLoggedIn);
      return of({ ok: true, passwordChangeRequired: false });
    }

    return this.api.post<AuthUser>('/api/auth/login', { email, password }).pipe(
      tap((user) => this.persistUser(user, keepLoggedIn)),
      map((user) => ({
        ok: true,
        passwordChangeRequired: user.passwordChangeRequired === true,
      })),
      catchError(() => of({ ok: false, passwordChangeRequired: false })),
    );
  }

  refreshSession(selectedScopeId?: string | null): Observable<AuthUser | null> {
    const user = this._user();
    if (!user || this.api.isFakeShowcase) {
      return of(user);
    }

    const nextScopeId = selectedScopeId === undefined ? user.selectedScopeId : selectedScopeId;
    const keepLoggedIn = this.isCurrentSessionPersistent();
    this.persistUser({ ...user, selectedScopeId: nextScopeId ?? null }, keepLoggedIn);
    this._sessionRefreshing.set(true);

    return this.api.get<AuthUser>('/api/auth/session').pipe(
      tap((nextUser) => {
        this.persistUser(nextUser, keepLoggedIn);
        this._sessionRefreshing.set(false);
      }),
      catchError(() => {
        this._sessionRefreshing.set(false);
        this.logout();
        return of(null);
      }),
    );
  }

  setSelectedScope(scopeId: string | null): void {
    const user = this._user();
    if (!user) {
      return;
    }

    if (this.api.isFakeShowcase) {
      this.persistUser(
        {
          ...user,
          selectedScopeId: scopeId,
          effectiveRoles: user.baseRoles,
        },
        this.isCurrentSessionPersistent(),
      );
      return;
    }

    this.refreshSession(scopeId).subscribe();
  }

  changePassword(newPassword: string): Observable<boolean> {
    return this.api.post<AuthUser>('/api/auth/change-password', { newPassword }).pipe(
      tap((user) => this.persistUser(user, this.isCurrentSessionPersistent())),
      map(() => true),
      catchError(() => of(false)),
    );
  }

  acceptInvite(token: string, newPassword: string, keepLoggedIn: boolean): Observable<boolean> {
    return this.api.post<AuthUser>('/api/auth/invite/accept', { token, newPassword }).pipe(
      tap(() => {
        void keepLoggedIn;
        this.logout();
      }),
      map(() => true),
      catchError(() => of(false)),
    );
  }

  resolveInvite(token: string): Observable<AuthUser | null> {
    return this.api.post<AuthUser>('/api/auth/invite/resolve', { token }).pipe(
      catchError(() => of(null)),
    );
  }

  logout(): void {
    this._user.set(null);
    this._sessionRefreshing.set(false);
    sessionStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_KEY);
  }

  isAuthenticated(): boolean {
    const user = this._user();
    if (user?.expiresAt && user.expiresAt * 1000 <= Date.now()) {
      this.logout();
      return false;
    }

    return user !== null &&
      !user.passwordChangeRequired &&
      (this.api.isFakeShowcase || Boolean(user.accessToken));
  }

  hasDashboardAccess(): boolean {
    const user = this._user();
    if (!user) {
      return false;
    }

    return user.hasGlobalScopeAccess || flattenScopes(user.accessibleScopes).length > 0;
  }

  canManageSettings(): boolean {
    return this._user()?.canManageSettings === true;
  }

  isOwnerEquivalent(): boolean {
    const user = this._user();
    return user?.isGenesis === true || user?.baseRoles.includes('owner') === true;
  }

  currentEffectiveRoles(): UserRole[] {
    return this._user()?.effectiveRoles ?? [];
  }
}
